import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ServicesProvidedService } from '../../dashboard/servicesComponets/service/service.service';
import { DisasterAdminService } from '../../dashboard/servicesComponets/disaster/disaster.service';
import { GeneralOrganizationBackgroundSurveyService } from '../../dashboard/servicesComponets/surveys/generalOrganizationBackground/general-organization-background.service';
import { DisasterSpecificSurveyService } from '../../dashboard/servicesComponets/surveys/disasterSpecificSurvey/disaster-specific-survey.service';
import { LocationSpecificSurveyService } from '../../dashboard/servicesComponets/surveys/locationSpecificSurvey/location-specific-survey.service';
import { CurrencyService } from '../../sharedServices/currencyService/currency.service';
import { CurrencyPickerService } from '../../header/currencyPicker/currency-picker.service';
import { PageH1Service } from '../../common/page-h1/page-h1.service';
import { GlobalLoaderFacade as SlimLoadingBarService } from '../../sharedServices/globalLoaderFacade/global-loader-facade.service';
import { DisasterAboutService } from './disaster-about.service';
import { ResponseProfileComponent } from '../responseProfile/response-profile.component';
import { validateResponseField } from '../survey-response-helpers';
import {
  Collection,
  ConvertedMoney,
  Dictionary,
  Disaster,
  DisasterSpecificSurveyInput,
  LocationSpecificSurveyInput,
  Money,
  MoneyConversionOutput,
  ModalAction,
  OrgDisasterScore,
  OrgLocationScore,
  Service,
} from '../../models';
import { AppSettings } from '../../common/config';
import { handleErrors } from '../../common/error';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { GeneralOrganizationBackgroundSurveyInput } from 'app/dashboard/servicesComponets/surveys/generalOrganizationBackground/general-organization-background.model';
import { getDisasterIdFromUrl, getDisasterRoute, resolveLegacyDisasterId } from '../../common/disaster-url';

@Component({
  selector: 'disaster-about',
  templateUrl: './disaster-about.component.html',
  styleUrls: ['./disaster-about.component.css', '../common-search.css'],
})
export class DisasterAboutComponent extends ResponseProfileComponent implements OnInit {
  public disaster: Disaster;
  public dssArray: DisasterSpecificSurveyInput[];
  public lssArray: LocationSpecificSurveyInput[];
  public disasterSummary: any; // used to store consolidated info from dss
  public respondingOrgs: any[];
  public locationOrgs: any[];
  public csloCount: number[];
  public ingoCount: number[];
  public widgetText: string;

  public AppSettings = AppSettings;

  private allAvailableServices: Service[];

  public disasterScoreActions: EventEmitter<OrgDisasterScore> = new EventEmitter<OrgDisasterScore>();
  public organizationDisasterScores: OrgDisasterScore[];

  public locationScoreActions: EventEmitter<OrgLocationScore> = new EventEmitter<OrgLocationScore>();
  public organizationLocationScores: OrgLocationScore[];

  public itemsPerPageArg = 10;
  public selectedPage = 0;

  subscriptions: Dictionary<Subscription> = {
    route: null,
    currency: null,
  };

  currencySubFirstRun = true;

  money: Collection<ConvertedMoney> = {
    singles: {},
    arrays: {
      respondingBudget: [],
      respondingRaised: [],
      respondingSpent: [],
      locationBudget: [],
    },
  };

  donateModalActions = new EventEmitter<string | ModalAction>();
  tippingModalActions = new EventEmitter<string | ModalAction>();
  isSubmitting = false;

  loading: boolean;
  oName: string;
  oID: string;
  oDonateUrl: string;

  constructor(
    private servicesProvidedService: ServicesProvidedService,
    private disasterAdminService: DisasterAdminService,
    private gobService: GeneralOrganizationBackgroundSurveyService,
    private dssSurveyService: DisasterSpecificSurveyService,
    private lssSurveyService: LocationSpecificSurveyService,
    private route: ActivatedRoute,
    private router: Router,
    private globalLoader: SlimLoadingBarService,
    protected toastr: ToastrService,
    protected translateService: TranslateService,
    private disasterAboutService: DisasterAboutService,
    private currencyService: CurrencyService,
    private currencyPickerService: CurrencyPickerService,
    pageH1Service: PageH1Service
  ) {
    super(currencyService, currencyPickerService, translateService, toastr, pageH1Service);
    this.addSubscription('route', this.getRouteSubscription.bind(this));
  }

  ngOnInit() {
    this.loading = true;
    this.globalLoader.start();
  }

  onItemsPerPageChange(updatedVal: number) {
    // if itemsPerArg is a string, there is undefined and wacky behavior exhibited by pagination
    this.itemsPerPageArg = +updatedVal;
    this.selectedPage = 0;
  }

  private createDisasterSurveysObservable(disasterID, displayDss): Observable<any> {
    if (displayDss) {
      return this.dssSurveyService.getSurveysByDisaster(disasterID);
    }
    return of([]);
  }

  private createLocationSurveysObservable(disasterID, displayDss): Observable<any> {
    if (!displayDss) {
      return this.lssSurveyService.getSurveysByRelatedDisaster(disasterID);
    }
    return of([]);
  }

  private getRouteSubscription(): Subscription {
    const routeObservable = this.route.params.pipe(
      switchMap((params: Params) => {
        return this.resolveDisasterId(params).pipe(
          switchMap((disasterId) => {
            const servicesProvidedObservable = this.servicesProvidedService.getServices();
            const csloCountObservable = this.disasterAboutService.getOrganizationCount(
              disasterId,
              AppSettings.ORGANIZATION_TYPES['CSLO']
            );
            const ingoCountObservable = this.disasterAboutService.getOrganizationCount(
              disasterId,
              AppSettings.ORGANIZATION_TYPES['INGO']
            );

            return this.disasterAdminService.getDisaster(disasterId).pipe(
              map((disaster) => {
                if (!disaster || disaster.disasterId !== disasterId) {
                  throw new Error(`Unexpected disaster detail response for ID: ${disasterId}`);
                }
                return disaster;
              }),
              switchMap((disaster) => {
                const disasterSurveysObservable = this.createDisasterSurveysObservable(
                  disaster.disasterId,
                  disaster.displayDss
                );
                const locationSurveysObservable = this.createLocationSurveysObservable(
                  disaster.disasterId,
                  disaster.displayDss
                );
                return forkJoin({
                  disaster: of(disaster),
                  surveys: forkJoin({ disasterSurveysObservable, locationSurveysObservable }),
                  servicesProvided$: servicesProvidedObservable,
                  csloCount$: csloCountObservable,
                  ingoCount$: ingoCountObservable,
                }).pipe(
                  map((result) => ({
                    disaster: result.disaster,
                    disasterSurveys: result.surveys.disasterSurveysObservable,
                    locationSurveys: result.surveys.locationSurveysObservable,
                    servicesProvided: result.servicesProvided$,
                    csloCount: result.csloCount$,
                    ingoCount: result.ingoCount$,
                  }))
                );
              })
            );
          })
        );
      })
    );

    return routeObservable.subscribe(
      (success) => {
        this.onDisasterLoaded(success);
        this.globalLoader.complete();
        const snapshot = this.route.snapshot;
        const canonicalRoute = getDisasterRoute(success.disaster);
        if (snapshot.params['id'] !== String(canonicalRoute[1]) || snapshot.params['slug'] !== canonicalRoute[2]) {
          this.router.navigate(canonicalRoute, {
            replaceUrl: true,
            queryParams: snapshot.queryParams,
            fragment: snapshot.fragment,
          }).catch((error) =>
            handleErrors(error, this.translateService, this.toastr, 'DisasterAboutComponent-DISASTER_INFO_NO_NAME')
          );
        }
      },
      (error) => {
        this.globalLoader.complete();
        this.loading = false;
        handleErrors(error, this.translateService, this.toastr, 'DisasterAboutComponent-DISASTER_INFO_NO_NAME');
      }
    );
  }

  private resolveDisasterId(params: Params): Observable<number> {
    const value = params['id'] || params['slug'];
    const id = getDisasterIdFromUrl(value);
    if (id !== null) {
      return of(id);
    }
    if (params['id']) {
      throw new Error(`Invalid disaster ID in URL: ${value}`);
    }

    return this.disasterAdminService.getDisasters().pipe(
      map((disasters) => resolveLegacyDisasterId(disasters, value))
    );
  }

  private getCurrencySubscription(): Subscription {
    return this.setupCurrencyContext().subscribe(([conversions, formats]) => {
      this.setCurrencies(conversions.currency, formats);
      this.setMoney(conversions);
    });
  }

  private onDisasterLoaded(data: any): void {
    // Slim loading bar
    this.globalLoader.complete();
    this.loading = false;
    this.disaster = data.disaster;
    this.setPageHeading(
      this.disaster?.name ||
        this.translateService.instant('DisasterAboutComponent-DISASTER_INFO_NO_NAME')
    );
    this.dssArray = data.disasterSurveys
      .map((dss) => dss.scoreDto.surveyDto)
      .filter((survey) => survey && survey.surveySubmissionId !== null);
    this.lssArray = data.locationSurveys
      .map((lss) => lss.scoreDto.surveyDto)
      .filter((survey) => survey && survey.surveySubmissionId !== null);
    this.allAvailableServices = data.servicesProvided;
    this.csloCount = data.csloCount.responseData.count;
    this.ingoCount = data.ingoCount.responseData.count;

    if (this.disaster.displayDss) {
      this.organizationDisasterScores = data.disasterSurveys
        .filter((dss) => dss.scoreDto && dss.scoreDto.surveyDto)
        .map((dss) => {
          return <OrgDisasterScore>{
            organizationId: dss.scoreDto.surveyDto.organizationId,
            organizationName: dss.scoreDto.surveyDto.organizationName,
            disasterName: dss.scoreDto.surveyDto.disaster ? dss.scoreDto.surveyDto.disaster.name : null,
            updateScore: dss.scoreDto.updateScore,
            adminEditScore: dss.scoreDto.adminEditScore,
            directServiceScore: dss.scoreDto.directServiceScore,
            refusedQuestionScore: dss.scoreDto.refusedQuestionScore,
            localStaffScore: dss.scoreDto.localStaffScore,
            localStaffCountScore: dss.scoreDto.localStaffCountScore,
          };
        });
    } else {
      this.organizationLocationScores = data.locationSurveys
        .filter((lss) => lss.scoreDto && lss.scoreDto.surveyDto)
        .map((lss) => {
          return <OrgLocationScore>{
            organizationName: lss.scoreDto.surveyDto.organizationName,
            organizationId: lss.scoreDto.surveyDto.organizationId,
            countryName: lss.scoreDto.surveyDto.country ? lss.scoreDto.surveyDto.country.name : null,
            countryId: lss.scoreDto.surveyDto.country ? lss.scoreDto.surveyDto.country.countryId : null,
            updateScore: lss.scoreDto.updateScore,
            adminEditScore: lss.scoreDto.adminEditScore,
            directServiceScore: lss.scoreDto.directServiceScore,
            refusedQuestionScore: lss.scoreDto.refusedQuestionScore,
            localStaffScore: lss.scoreDto.localStaffScore,
          };
        });
    }
    // tslint:disable-next-line:no-increment-decrement
    for (let i = 0; i < this.disaster.services.length; i++) {
      const foundService = this.allAvailableServices.find((r) => r.serviceId === this.disaster.services[i].serviceId);

      if (foundService) {
        this.disaster.services[i] = foundService;
      }
    }
    // initialize
    this.disasterSummary = {
      moneyRaised: { amount: 0, currency: AppSettings.DEFAULT_CURRENCY },
      moneySpent: { amount: 0, currency: AppSettings.DEFAULT_CURRENCY },
      staffCount: 0,
      percentageSpent: null,
    };

    if (this.disaster.displayDss) {
      this.respondingOrgs = [];

      const respondingDssArray: DisasterSpecificSurveyInput[] = this.dssArray.filter(
        (dss) => dss.organizationRespondingToDisaster
      );

      const respondingGobObservableArray: Observable<GeneralOrganizationBackgroundSurveyInput>[] = respondingDssArray.map(
        (dss) => {
          return this.gobService.getMostRecentSurvey(dss.organizationId).pipe(
            catchError(() => {
              console.log('Error loading the latest GOB survey for ' + dss.organizationId);
              return of(null);
            })
          );
        }
      );

      forkJoin(respondingGobObservableArray).subscribe((gobArray: GeneralOrganizationBackgroundSurveyInput[]) => {
        respondingDssArray.forEach((dss, i) => {
          const org = {
            id: dss.organizationId,
            name: dss.organizationName,
            staffCount: this.getStaffCount(dss),
            serviceList: this.getServiceList(dss).slice(0, 3),
            isSolicitingDonations: dss.solicitDonation && dss.solicitDonation.value.isCurrentlySolicitingDonation,
            hqLocation: dss.organizationDto.country,
            donateButtonDisabled: dss.donateButtonDisabled,
            donateUrl: '',
          };
          const gob: GeneralOrganizationBackgroundSurveyInput = gobArray.find(
            (gob) => gob.organizationId === dss.organizationId
          );
          if (gob.donateUrl !== null) {
            org.donateUrl = gob.donateUrl && gob.donateUrl.value;
          }

          const respondingFunds: Dictionary<Money> = {
            respondingBudget: validateResponseField<Money>(gob.overallAnnualBudget),
            respondingRaised: validateResponseField<Money>(dss.raisedFund),
            respondingSpent: validateResponseField<Money>(dss.spentFund),
          };

          for (const [category, value] of Object.entries(respondingFunds)) {
            this.money.arrays[category][i] = new ConvertedMoney(value);
          }

          this.disasterSummary.staffCount += org.staffCount;

          this.respondingOrgs[i] = org;
        });
        this.addSubscription('currency', this.getCurrencySubscription.bind(this));
      });
    } else {
      const locationGobObservableArray: Observable<GeneralOrganizationBackgroundSurveyInput>[] = this.lssArray.map(
        (lss) => {
          return this.gobService.getMostRecentSurvey(lss.organizationId).pipe(
            catchError(() => {
              console.log('Error loading the latest GOB survey for ' + lss.organizationId);
              return of(null);
            })
          );
        }
      );

      this.locationOrgs = [];

      forkJoin(locationGobObservableArray).subscribe((gobArray: GeneralOrganizationBackgroundSurveyInput[]) => {
        this.lssArray.forEach((lss, i) => {
          const org = {
            id: lss.organizationId,
            name: lss.organizationName,
            location: lss.country.name,
            locationId: lss.country.countryId,
            staffCount: this.getLocationOrgStaffCount(lss),
            serviceList: this.getLocationOrgServiceList(lss).slice(0, 3),
            hqLocation: lss.organizationDto.country,
            donateButtonDisabled: lss.donateButtonDisabled,
            donateUrl: '',
          };

          const gob: GeneralOrganizationBackgroundSurveyInput = gobArray.find(
            (g) => g && g.organizationId === lss.organizationId
          );

          if (gob && gob.donateUrl !== null) {
            org.donateUrl = gob.donateUrl && gob.donateUrl.value;
          }

          this.locationOrgs[i] = org;
          this.money.arrays.locationBudget[i] = new ConvertedMoney(validateResponseField<Money>(lss.overallBudget));
        });
        this.addSubscription('currency', this.getCurrencySubscription.bind(this));
      });
    }
  }

  openDonateModal(oName: string, oID: string, oDonateUrl: string) {
    this.oName = oName;
    this.oID = oID;
    this.oDonateUrl = oDonateUrl;
    
    this.donateModalActions.emit({ action: 'modal', params: ['open'] });
  }

  private setMoney(conversions: MoneyConversionOutput): void {
    const money: Collection<ConvertedMoney> = conversions.money;
    if (money.arrays) {
      if (money.arrays.respondingBudget && money.arrays.respondingBudget.length) {
        money.arrays.respondingBudget.forEach((budget, i) => {
          this.respondingOrgs[i]['budget'] = budget.converted;
        });
      }
      if (money.arrays.respondingRaised && money.arrays.respondingRaised.length) {
        money.arrays.respondingRaised.forEach((raised, i) => {
          this.respondingOrgs[i]['raised'] = raised.converted;
        });
      }
      if (money.arrays.respondingSpent && money.arrays.respondingSpent.length) {
        money.arrays.respondingSpent.forEach((spent, i) => {
          this.respondingOrgs[i]['spent'] = spent.converted;
        });
      }
      if (money.arrays.locationBudget && money.arrays.locationBudget.length) {
        money.arrays.locationBudget.forEach((budget, i) => {
          this.locationOrgs[i]['budget'] = budget.converted;
        });
      }
    }
    this.money = money;
    if (!conversions.hasError) {
      this.setFundraisingStats();
    }
  }

  private setFundraisingStats(): void {
    // populate respondingOrgs displayed properties with converted Money amounts
    const sumConvertedMoney = function (arr: ConvertedMoney[]): number {
      if (arr.length) {
        return arr
          .map((money: ConvertedMoney) => {
            if (money.converted && money.converted.amount !== null && money.converted.amount !== undefined) {
              return money.converted.amount;
            }
            return 0;
          })
          .reduce((sum, value) => sum + value);
      }
      return null;
    };
    const totalRaised: Money = {
      amount: sumConvertedMoney(this.money.arrays.respondingRaised),
      currency: this.currency.code,
    };
    const totalSpent: Money = {
      amount: sumConvertedMoney(this.money.arrays.respondingSpent),
      currency: this.currency.code,
    };
    this.disasterSummary.moneyRaised = totalRaised;
    this.disasterSummary.moneySpent = totalSpent;
    if (this.disasterSummary.percentageSpent === null || this.disasterSummary.percentageSpent === undefined) {
      this.disasterSummary.percentageSpent =
        totalRaised && totalSpent ? (100 * totalSpent.amount) / totalRaised.amount : 0;
    }
  }

  private getStaffCount(dssSurvey: DisasterSpecificSurveyInput): number {
    let staffCount = 0;
    if (!dssSurvey) {
      return staffCount;
    }

    for (const staffType of ['localStaff', 'nonLocalStaff']) {
      if (dssSurvey[staffType] && dssSurvey[staffType].value) {
        staffCount =
          staffCount +
          dssSurvey[staffType].value.mgmtStaffCount +
          dssSurvey[staffType].value.professionalStaffCount +
          dssSurvey[staffType].value.supportStaffCount;
      }
    }

    return staffCount;
  }

  private getLocationOrgStaffCount(lssSurvey: LocationSpecificSurveyInput): number {
    let staffCount = 0;
    if (!lssSurvey) {
      return staffCount;
    }

    for (const staffType of ['localStaff', 'nonLocalStaff']) {
      if (lssSurvey[staffType]) {
        staffCount =
          staffCount +
          lssSurvey[staffType].value.mgmtStaffCount +
          lssSurvey[staffType].value.professionalStaffCount +
          lssSurvey[staffType].value.supportStaffCount;
      }
    }

    return staffCount;
  }

  private getServiceList(dssSurvey: DisasterSpecificSurveyInput): string[] {
    const serviceList = [];

    if (dssSurvey && dssSurvey.serviceList) {
      for (const service of dssSurvey.serviceList.value) {
        if (service.directProportion || service.indirectProportion) {
          serviceList.push(service.serviceProvided.name);
        }
      }
    }

    return serviceList;
  }

  private getLocationOrgServiceList(lssSurvey: LocationSpecificSurveyInput): string[] {
    const serviceList = [];

    if (!lssSurvey || !lssSurvey.serviceList) {
      return serviceList;
    }

    for (const service of lssSurvey.serviceList.value) {
      if (service.directProportion || service.indirectProportion) {
        serviceList.push(service.serviceProvided.name);
      }
    }

    return serviceList;
  }

  openDssScoreModal(orgId: number) {
    this.disasterScoreActions.emit(this.organizationDisasterScores.find((score) => score.organizationId === orgId));
  }

  openLssScoreModal(orgId: number, countryId: number) {
    this.locationScoreActions.emit(
      this.organizationLocationScores.find((score) => score.organizationId === orgId && score.countryId === countryId)
    );
  }

  displayWidget() {
    if (this.widgetText) {
      this.widgetText = '';
    } else {
      this.widgetText = this.disasterAboutService.getWidgetText(this.disaster.disasterId);
    }
  }
}
